import { SavingsGoalResponse } from "../../../services";
import { SavingsGoalSeries } from "./types";

export const newestOf = (series: SavingsGoalSeries) =>
  series.goals[series.goals.length - 1];

const latestTargetDateOf = (series: SavingsGoalSeries) =>
  newestOf(series).targetDate;

export const buildSavingsGoalSeries = (goals: SavingsGoalResponse[]) => {
  const pastGoals = goals.filter((goal) => goal.closedAt !== null);
  const goalById = new Map(goals.map((goal) => [goal.id, goal]));
  const pastById = new Map(pastGoals.map((goal) => [goal.id, goal]));
  const successorByPredecessor = new Map<number, SavingsGoalResponse>();
  pastGoals.forEach((goal) => {
    if (goal.previousGoalId !== null && pastById.has(goal.previousGoalId)) {
      successorByPredecessor.set(goal.previousGoalId, goal);
    }
  });

  const series = pastGoals
    .filter(
      (goal) =>
        goal.previousGoalId === null || !pastById.has(goal.previousGoalId),
    )
    .map((first): SavingsGoalSeries => {
      const chain = [first];
      let next = successorByPredecessor.get(first.id);
      while (next) {
        chain.push(next);
        next = successorByPredecessor.get(next.id);
      }
      return { id: first.id, goals: chain };
    })
    .sort((a, b) => latestTargetDateOf(b).localeCompare(latestTargetDateOf(a)));

  const goalIdsWithSuccessor = new Set(
    goals.flatMap((goal) =>
      goal.previousGoalId === null ? [] : [goal.previousGoalId],
    ),
  );

  return {
    series,
    predecessorOf: (goalId: number) => {
      const previousGoalId = goalById.get(goalId)?.previousGoalId ?? null;
      return previousGoalId === null ? undefined : pastById.get(previousGoalId);
    },
    hasSuccessor: (goalId: number) => goalIdsWithSuccessor.has(goalId),
  };
};
